import React, { useMemo, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Sparkles, Loader2, Eye, EyeOff, ArrowLeft } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { PasswordStrengthMeter, MIN_PASSWORD_LENGTH } from "@/components/bi/PasswordStrengthMeter";

export default function ResetPassword() {
  const [params] = useSearchParams();
  const nav = useNavigate();
  const { setUser } = useAuth();
  const token = useMemo(() => params.get("token") || "", [params]);
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [showPw, setShowPw] = useState(false);
  const [pwOk, setPwOk] = useState(false);
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (password.length < MIN_PASSWORD_LENGTH) { toast.error(`Password must be at least ${MIN_PASSWORD_LENGTH} characters`); return; }
    if (!pwOk) { toast.error("Please choose a stronger password"); return; }
    if (password !== confirm) { toast.error("Passwords do not match"); return; }
    setLoading(true);
    try {
      const { data } = await api.post("/auth/password/reset", { token, new_password: password });
      if (data?.user) setUser(data.user);
      toast.success("Password updated. You're signed in.", { duration: 2000 });
      nav("/");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Reset failed");
    } finally { setLoading(false); }
  };

  if (!token) {
    return (
      <div className="min-h-screen flex items-center justify-center bi-canvas p-6">
        <div className="max-w-sm text-center">
          <h2 className="font-display text-2xl">Invalid reset link</h2>
          <p className="text-sm text-[color:var(--ink-3)] mt-2">The reset link is missing or malformed. Please request a new one.</p>
          <Link to="/forgot-password" className="inline-block mt-4 text-[color:var(--brand-ocean)] font-medium">Request new link</Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex bi-canvas">
      <div className="flex-1 flex items-center justify-center p-6">
        <div className="w-full max-w-sm">
          <div className="flex items-center gap-2 mb-8">
            <div className="w-9 h-9 rounded-xl bg-[color:var(--brand-ocean)] flex items-center justify-center"><Sparkles className="w-5 h-5 text-white" /></div>
            <span className="font-display text-lg font-semibold">Eazy Analytics</span>
          </div>
          <h2 className="font-display text-2xl text-[color:var(--ink)]">Set a new password</h2>
          <p className="text-sm text-[color:var(--ink-3)] mt-1">Choose something you'll remember. You'll be signed in automatically.</p>
          <form onSubmit={submit} className="mt-6 space-y-4">
            <div>
              <Label className="text-[color:var(--ink-2)]">New password</Label>
              <div className="relative mt-1.5">
                <Input data-testid="reset-password" type={showPw ? "text" : "password"} value={password} onChange={(e) => setPassword(e.target.value)} className="bg-[color:var(--surface)] pr-10" required autoFocus minLength={MIN_PASSWORD_LENGTH} />
                <button type="button" data-testid="reset-password-toggle" onClick={() => setShowPw((v) => !v)} aria-label={showPw ? "Hide password" : "Show password"} className="absolute inset-y-0 right-0 flex items-center px-3 text-[color:var(--ink-3)] hover:text-[color:var(--ink)]">
                  {showPw ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
              <PasswordStrengthMeter password={password} onChange={(s) => setPwOk(s.valid)} />
            </div>
            <div>
              <Label className="text-[color:var(--ink-2)]">Confirm password</Label>
              <Input data-testid="reset-password-confirm" type={showPw ? "text" : "password"} value={confirm} onChange={(e) => setConfirm(e.target.value)} className="mt-1.5 bg-[color:var(--surface)]" required />
            </div>
            <Button data-testid="reset-submit" type="submit" disabled={loading} className="w-full bg-[color:var(--brand-ocean)] hover:bg-[#1E3AD9] text-white">
              {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : "Update password & sign in"}
            </Button>
          </form>
          <Link to="/login" className="inline-flex items-center gap-1.5 text-[color:var(--brand-ocean)] text-sm font-medium mt-6"><ArrowLeft className="w-4 h-4" />Back to sign in</Link>
        </div>
      </div>
    </div>
  );
}
